import { useEffect, useMemo, useState } from 'react';

import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd/es/menu';
import { Spin } from 'antd';
import { useSimpleModal } from '../Modal/Simple';
import { PostShort, usePostDelete, usePostHide, usePostRestore } from '../../../hooks/api/posts';
import { CommentData, useCommentsGet } from '../../../hooks/api/comments';
import PostOrCommentTemplate, { useUniqueData } from '../PostOrCommentTemplate';
import Comment from '../Comment';
import ReasonModal from '../ReasonModal';
import styles from '../PostOrCommentTemplate/index.module.scss';

export interface CommentProps {
  data: PostShort;
  setPage: (page: number) => void;
}

export default function Post({ data, setPage }: CommentProps): JSX.Element {
  const { modal, contextHolder } = useSimpleModal();
  const [open, setOpen] = useState(false);
  const [openHidePost, setOpenHidePost] = useState(false);
  const commentsGet = useCommentsGet();
  const postDelete = usePostDelete(data.id);
  const postHide = usePostHide();
  const postRestore = usePostRestore();
  const [commentPage, setCommentPage] = useState(1);
  const [commentListAcc] = useUniqueData<CommentData>(commentsGet, commentPage);

  const loadComments = (trigger = false) => {
    if (open && (commentListAcc.list.length === 0 || trigger === true)) {
      commentsGet.fetch({
        postId: data.id,
        page: commentPage,
      });
    }
  };

  useEffect(() => {
    if (postDelete.data && !postDelete.error && !postDelete.loading) {
      setPage(1);
    }
  }, [postDelete.data]);

  useEffect(() => {
    if (postHide.data && !postHide.error && !postHide.loading) {
      setOpenHidePost(false);
      setPage(1);
    }
  }, [postHide.data]);

  useEffect(() => {
    if (postRestore.data && !postRestore.error && !postRestore.loading) {
      setPage(1);
    }
  }, [postRestore.data]);

  useEffect(loadComments, [open, commentPage]);

  const confirmDelete = () => {
    if (data.id) {
      modal.confirm({
        title: 'Are you sure you want to Delete the post?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => postDelete.fetch(),
        okText: 'Delete',
      });
    }
  };

  const hidePost = (reason: string) => {
    postHide.fetch({ reason }, `${data.id}/hide`);
  };

  const confirmRestore = () => {
    modal.confirm({
      title: 'Are you sure you want to Publish the post?',
      icon: <ExclamationCircleOutlined />,
      onOk: () => postRestore.fetch(undefined, `${data.id}/restore`),
      okText: 'Publish',
    });
  };

  const dropdownMenu: MenuProps = useMemo(
    () => ({
      items: [
        // {
        //   key: '1',
        //   label: 'Edit',
        //   onClick: () => navigate(`/posts/${data.id}/update`),
        // },
        {
          key: '2',
          label: 'Delete',
          onClick: confirmDelete,
        },
        !data.isHidden
          ? {
            key: '3',
            label: 'Hide',
            onClick: () => setOpenHidePost(true),
          }
          : {
            key: '3',
            label: 'Publish',
            onClick: confirmRestore,
          },
      ],
    }),
    [data],
  );

  const childrenList = useMemo(
    () => [
      ...(commentListAcc.list.map((comment) => (
        <Comment
          setPage={(newPage) => {
            setCommentPage(newPage);
            loadComments(true);
          }}
          key={comment.id}
          data={comment}
        />
      )) || []),
      commentsGet.loading ? (
        <Spin key="loading" indicator={<LoadingOutlined spin />} className={styles.spin} />
      ) : undefined,
    ].filter((value) => value),
    [commentListAcc.list, commentsGet.loading],
  );

  return (
    <>
      <PostOrCommentTemplate
        loading={commentsGet.loading}
        open={open}
        setOpen={setOpen}
        nextPage={() => setPage(commentPage + 1)}
        data={data}
        type="posts"
        dropdownMenu={dropdownMenu}
        showMoreText={commentListAcc.lastMeta?.hasNextPage && !commentsGet.loading ? 'Show more comment' : undefined}
        childrenList={open ? childrenList : undefined}
      />
      <ReasonModal
        open={openHidePost}
        setOpen={setOpenHidePost}
        title="Hide post?"
        actionButtonText="Hide post"
        placeholder="Why is this post being hidden?"
        subtitle="The post will no longer be visible to other users."
        loading={postHide.loading}
        onSubmit={hidePost}
      />
      {contextHolder}
    </>
  );
}
